import { AxiosError, isAxiosError } from 'axios';
import { FormEvent, useEffect, useState } from 'react';
import PhoneInput, {
    Country,
    isPossiblePhoneNumber,
} from 'react-phone-number-input';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setShowNavigator } from '../../appSlice';
import { ReactComponent as ErrorIcon } from '../../assets/icons/RedError.svg';
import { generateHeaders } from '../../utils/api';
import { axios } from '../../utils/axios';
import { setStatusBarLight } from '../../utils/webview/messages';
import {
    selectLoginError,
    selectLoginToken,
    setLoginError,
    setLoginPhone,
    setLoginToken,
} from './slice';

const availableCountries: Array<Country> = ['US', 'CA', 'MX'];

const MfaPhone = () => {
    const dispatch = useAppDispatch();
    const token = useAppSelector(selectLoginToken);
    const loginError = useAppSelector(selectLoginError);
    const [phone, setPhone] = useState<string>();
    const [validNumber, setValidNumber] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dispatch(setShowNavigator(false));
        setStatusBarLight(true);
    }, [dispatch]);

    const validatePhoneNumber = (input: string | undefined) => {
        try {
            setPhone(input);
            setValidNumber(!!input && isPossiblePhoneNumber(input));
        } catch {
            setValidNumber(false);
        }
    };

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!phone || !validNumber) return;
        try {
            dispatch(setLoginError(null));
            setLoading(true);
            const result = await axios.post(
                `${process.env.REACT_APP_AUTH_ENDPOINT}/mfa/phone`,
                {
                    phone,
                    token,
                },
                { headers: await generateHeaders() }
            );
            if (isAxiosError(result.data?.axiosError))
                throw result.data.axiosError;
            if (result.data.errorType === 'MFA') {
                dispatch(setLoginToken(result.data.token));
                dispatch(setLoginPhone(result.data.phone));
                dispatch(setLoginError(null));
            }
        } catch (err) {
            let message = 'Something went wrong. Please try again.';
            if (isAxiosError(err)) {
                const axiosError = err as AxiosError<any>;
                if (axiosError.response?.status === 401) {
                    message = 'Invalid credentials. Please try again.';
                }
            }
            dispatch(setLoginError(message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <h1
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'end',
                    paddingBottom: 50,
                }}
                id="login_page_header"
            >
                Setup phone number
            </h1>
            <form className="form" onSubmit={onSubmit}>
                <p>Enter your phone number to receive a verification code.</p>
                <div className="mfa-phone__contact">
                    <PhoneInput
                        value={phone}
                        countries={availableCountries}
                        defaultCountry="US"
                        id="mfa_phone_input"
                        placeholder="Enter phone number"
                        addInternationalOption={false}
                        onChange={validatePhoneNumber}
                    />
                </div>
                {loginError && (
                    <div className="radio-error" style={{ marginTop: 5 }}>
                        <ErrorIcon
                            className="radio-error-icon"
                            id="login_page_error_icon"
                        />
                        {loginError}
                    </div>
                )}
                <button
                    type="submit"
                    className="button"
                    disabled={loading || !validNumber}
                    id="login_page_button"
                >
                    Continue
                </button>
            </form>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 2,
                    justifyContent: 'space-evenly',
                }}
            />
        </>
    );
};

export default MfaPhone;
